// Helpers de la "conquista" de rutas en el mapa en vivo: clasifican los viajes
// por territorio (./territories.js), calculan el % de dominación con los lobbies
// que ya trae el dashboard y proyectan los corredores para dibujarlos.
import { projection, MAP_WIDTH, MAP_HEIGHT } from './geoData'
import { coordLngLat, departureMs, passengerCount } from './tripUtils'
import { TERRITORIES, MARGIN_KM, DOMINATION_SEATS_PER_MONTH } from './territories'
import { buildTrack, sampleAt } from './battleGeometry'

// Ancho de la región pintada alrededor de cada corredor.
const REGION_KM = 80
const KM_PER_DEG_LAT = 111.32

const project = ({ lat, lng }) => {
  const p = projection([lng, lat])
  return p && Number.isFinite(p[0]) && Number.isFinite(p[1]) ? p : null
}

// Unidades del mapa (sin zoom) por km, a una latitud dada.
const unitsPerKm = lat => {
  const a = projection([-60, lat])
  const b = projection([-60, lat + 0.5])
  return Math.abs(b[1] - a[1]) / (0.5 * KM_PER_DEG_LAT)
}

const mid = (a, b) => [(a[0] + b[0]) / 2, (a[1] + b[1]) / 2]

// Trazo suavizado: curvas en las paradas para que la región no quede con esquinas.
const smoothPath = pts => {
  const f = p => `${p[0].toFixed(1)},${p[1].toFixed(1)}`
  if (pts.length === 2) return `M${f(pts[0])} L${f(pts[1])}`
  let d = `M${f(pts[0])} L${f(mid(pts[0], pts[1]))}`
  for (let i = 1; i < pts.length - 1; i++) d += ` Q${f(pts[i])} ${f(mid(pts[i], pts[i + 1]))}`
  return `${d} L${f(pts[pts.length - 1])}`
}

// Ancla de la etiqueta: mitad del trazo principal, con la normal que apunta al
// este (o al norte si el corredor va de este a oeste) para correrla al costado.
const labelAnchorOf = track => {
  const mid = sampleAt(track, track.length / 2)
  let nx = -mid.ty
  let ny = mid.tx
  if (Math.abs(nx) > 0.3 ? nx < 0 : ny > 0) {
    nx = -nx
    ny = -ny
  }
  return { x: mid.x, y: mid.y, nx, ny }
}

export const buildTerritoryShapes = (territories = []) =>
  territories
    .map(t => {
      const byName = new Map(t.stops.map(s => [s.name, s]))
      const named = t.paths || [t.stops.map(s => s.name)]
      const lines = named
        .map(path => path.map(name => byName.get(name)).filter(Boolean).map(project).filter(Boolean))
        .filter(l => l.length >= 2)
      if (!lines.length) return null
      const pts = lines.flat()
      const xs = pts.map(p => p[0])
      const ys = pts.map(p => p[1])
      const lat = t.stops.reduce((acc, s) => acc + s.lat, 0) / t.stops.length
      const width = REGION_KM * unitsPerKm(lat)
      const tracks = lines.map(l => buildTrack(l, width / 6))
      // Banderas en las ciudades cabecera (por defecto, las puntas del trazo principal).
      const firstPath = named.find(path => path.filter(name => byName.has(name)).length >= 2) || []
      const flagNames = t.flags || [firstPath[0], firstPath[firstPath.length - 1]]
      const flags = flagNames.map(name => byName.get(name)).filter(Boolean).map(project).filter(Boolean)
      return {
        ...t,
        lines: lines.map(smoothPath),
        tracks,
        width,
        flagPoints: flags,
        labelAnchor: labelAnchorOf(tracks[0]),
        bbox: [Math.min(...xs), Math.min(...ys), Math.max(...xs), Math.max(...ys)]
      }
    })
    .filter(Boolean)

// Centro y zoom de ZoomableGroup para que entren las formas dadas.
export const fitView = (shapes, { pad = 46, maxZoom = 8 } = {}) => {
  if (!shapes || !shapes.length) return null
  const x0 = Math.min(...shapes.map(s => s.bbox[0])) - pad
  const y0 = Math.min(...shapes.map(s => s.bbox[1])) - pad
  const x1 = Math.max(...shapes.map(s => s.bbox[2])) + pad
  const y1 = Math.max(...shapes.map(s => s.bbox[3])) + pad
  const zoom = Math.min(maxZoom, Math.max(1, Math.min(MAP_WIDTH / (x1 - x0), MAP_HEIGHT / (y1 - y0))))
  const center = projection.invert([(x0 + x1) / 2, (y0 + y1) / 2])
  return center ? { coordinates: center, zoom } : null
}

export const tierOf = domination => {
  if (domination >= 1) return { key: 'dominated', label: 'Dominada', color: 'var(--terr-dominated)' }
  if (domination >= 0.75) return { key: 'almost', label: 'Casi dominada', color: 'var(--terr-conquest)' }
  if (domination >= 0.25) return { key: 'conquest', label: 'En conquista', color: 'var(--terr-conquest)' }
  if (domination > 0) return { key: 'landing', label: 'Desembarco', color: 'var(--terr-conquest)' }
  return { key: 'unexplored', label: 'Por conquistar', color: 'var(--text-faint)' }
}

// 100% sólo cuando está dominada; con algún asiento pero <1% mostramos "<1%".
export const formatPct = domination => {
  const pct = Math.floor(Math.min(1, domination || 0) * 100)
  return pct === 0 && domination > 0 ? '<1%' : `${pct}%`
}

const toRad = deg => (deg * Math.PI) / 180
const haversineKm = (p1, p2) => {
  const dLat = toRad(p2.lat - p1.lat)
  const dLng = toRad(p2.lng - p1.lng)
  const a = Math.sin(dLat / 2) ** 2 + Math.cos(toRad(p1.lat)) * Math.cos(toRad(p2.lat)) * Math.sin(dLng / 2) ** 2
  return 6371 * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a))
}

const zoneOf = (territory, point, margin) => {
  let best = null
  let bestKm = Infinity
  territory.stops.forEach(stop => {
    const km = haversineKm(stop, point)
    if (km <= margin && km < bestKm) {
      best = stop
      bestKm = km
    }
  })
  return best ? best.zone : null
}

// Territorio de un viaje: las dos puntas cerca de paradas de zonas distintas.
export const territoryOfTrip = (trip, territories = TERRITORIES, margin = MARGIN_KM) => {
  const o = coordLngLat(trip?.Journey?.origin)
  const d = coordLngLat(trip?.Journey?.destination)
  if (!o || !d) return null
  const origin = { lat: o[1], lng: o[0] }
  const destination = { lat: d[1], lng: d[0] }
  const match = territories.find(t => {
    const zo = zoneOf(t, origin, margin)
    const zd = zoneOf(t, destination, margin)
    return zo && zd && zo !== zd
  })
  return match ? match.key : null
}

const WINDOW_DAYS = 14
const DAY_MS = 24 * 60 * 60 * 1000

// % de dominación de cada territorio con lo que trae /admin/lobbies (salidas de
// las últimas 2 semanas en adelante):
//   - last_14_days:  asientos de viajes no cancelados que salieron en los últimos 14 días.
//   - monthly_pace:  eso llevado a 30 días.
//   - pace:          max(monthly_pace, annualAvg). El promedio anual (fijo, ver
//                    territories.js) evita que una ruta de temporada pierda el
//                    territorio en temporada baja.
//   - domination:    pace / DOMINATION_SEATS_PER_MONTH, tope 1.
export const computeConquest = (trips = [], now = Date.now(), territories = TERRITORIES) => {
  const since = now - WINDOW_DAYS * DAY_MS
  const seats = {}
  trips.forEach(trip => {
    if (trip?.cancelled) return
    const dep = departureMs(trip)
    if (Number.isNaN(dep) || dep < since || dep > now) return
    const n = passengerCount(trip)
    if (!n) return
    const key = territoryOfTrip(trip, territories)
    if (key) seats[key] = (seats[key] || 0) + n
  })
  return territories.map(t => {
    const last14 = seats[t.key] || 0
    const monthlyPace = Math.round((last14 * 30) / WINDOW_DAYS)
    const annualAverage = t.annualAvg || 0
    const pace = Math.max(monthlyPace, annualAverage)
    return {
      ...t,
      last_14_days: last14,
      monthly_pace: monthlyPace,
      annual_average: annualAverage,
      pace,
      domination: Math.min(1, pace / DOMINATION_SEATS_PER_MONTH)
    }
  })
}
